/**
 * transport/whitelist.ts —— **单入口白名单**（U-921／U-927；判据 web_no_second_egress）。
 * 纪律：一切网络访问只经本目录；下游只许**白名单内同源相对路径**（CONTRACT §2）：
 *      `EGRESS_PATHS` 逐条声明（当前 9 条：`/stream`·`/events`·`/api/auth/{login,logout,me}`·
 *      `/api/sessions`·`/api/models`·`/api/files`·`/api/stt`）——链内 WS 下行/上行＝`/stream`，
 *      写面落账＝`POST /events`，其余＝入口侧读/管理面。
 *      出现第二条实装通道（含 SSE）或直连上游 ⇒ 一律拒（红向口径）。
 */
import { E_CODES, TransportError } from "./errors";

/** 唯一实装通道端点（CONTRACT §2；★20261012 B0/B11：SSE 回退位已删——判据本义「禁第二实装通道」不变） */
export const EGRESS_PATHS = {
  // ★F-25（20261019·册 §6 位 transport「不含第二通道」）：审批裁决旁路 **已删**——
  //   原 `approvals: "/api/approvals"` 只为 `transport/approvals.ts::submitEscalateDecision` 服务，
  //   而升级（escalate）在 `cards/decision.ts` 已**走写面 `POST /events` 落账**（单一口径）；
  //   「升级＝写面单口径」定案 ⇒ 删旁路出网位与件（审批卡**读面** `GET /api/approvals` 属入口侧，
  //   不在前端出网白名单内，不由此处承载）。
  ws: "/stream",
  write: "/events",
  // 入口侧会话面（`CONTRACT` §2／§6）：**同源相对路径**，凭据由浏览器 cookie 自动携带，
  // 前端**不产生、不持有**密钥或签名（浏览器侧零凭据）。
  login: "/api/auth/login",
  logout: "/api/auth/logout",
  me: "/api/auth/me",
  // ★20261005 会话面（**入口侧**会话管理；与 `/api/auth/*` 同族）：列表／新建。
  // 依据＝大王令「新建会话失败」；链内写面唯一出口仍是 `/events`（本条不是写面通道）。
  sessions: "/api/sessions",
  // ★20261005 报障①「添加模型无法保存」：模型目录面（入口侧写面四动词）——集合路径，单件走 `assertEgressWithin`。
  models: "/api/models",
  // ★F3d（20261006）：产物/附件**读面**（`GET /api/files`；按会话可寻址）——**只读**，不是写通道
  //   （写面唯一出口仍是 `/events`；上传走 WS 上行 `kind:"attachment"`）。中栏附件条目名由它取回。
  files: "/api/files",
  // ★FE2（账行 L-FE2-3；20261011）：语音转写面 `POST /api/stt`——**同源相对路径**，
  //   与 `/api/auth/*`／`/api/models` 同族（入口侧登录后读面；**不是**链内写面：写面唯一出口
  //   仍是 `/events`）。后端路由同批入册（`api/routes.py::STT_ROUTES` ⇄ `ENTRY_OUTLETS`）。
  stt: "/api/stt",
} as const;

/** ★F3d：各集合读面**允许的查询键**（键白名单；值一律 encodeURIComponent ⇒ 禁注入/禁越权参数） */
/** ★20261006 右栏产物线：**单件子资源**（声明制——不是放宽前缀，而是逐项声明的合法子路径） */
export const EGRESS_SUBPATHS: Record<string, readonly string[]> = {
  files: ["pages", "content", "plain"],
};

export const EGRESS_QUERY_KEYS: Record<string, readonly string[]> = {
  files: ["session_id", "source", "offset", "limit"],
};

export type EgressKind = keyof typeof EGRESS_PATHS;

/**
 * 校验出网目标：只认同源相对路径且逐字命中白名单。
 * 拒：绝对 URL（上游直连）、协议相对（//host）、一切非白名单路径（含 /sse、/stream/x）。
 */
export function assertEgress(kind: EgressKind, path: string): string {
  const expected = EGRESS_PATHS[kind];
  const ok = path === expected;
  if (!ok) {
    throw new TransportError(
      E_CODES.EGRESS_FORBIDDEN, "egress",
      "出网目标越出单入口白名单：" + path,
      expected,
    );
  }
  return expected;
}

/**
 * WS 连接地址（同源派生；cursor 非空 ⇒ 带回放起点，等价 Last-Event-ID）。
 * 纪律：host 只来自当前 location（不落常量上游），杜绝第二上游。
 */
/**
 * `/stream` 入站 URL：游标（断线回放起点）＋**会话容器**（★C-Ⅷ：读面 `$active_session` 绑定）。
 *
 * 两参数同形同位（query 位、逐条白名单路径不变——仍只出网到 `/stream`）；未给 ⇒ 不出该键
 * （不造假值：无游标＝全量、无会话＝取数如实为空）。
 */
export function wsUrl(
  origin: { protocol: string; host: string },
  cursor: string | null,
  session?: string | null,
): string {
  const scheme = origin.protocol === "https:" ? "wss" : "ws";
  const base = scheme + "://" + origin.host + assertEgress("ws", EGRESS_PATHS.ws);
  const params: string[] = [];
  if (cursor) params.push("cursor=" + encodeURIComponent(cursor));
  if (session) params.push("session=" + encodeURIComponent(session));
  return params.length > 0 ? base + "?" + params.join("&") : base;
}

/**
 * 前缀式白名单校验（**只给需要单件路径的集合面**；仍是逐字前缀，禁通配）：
 * 允许 `base`（集合）或 `base/<单段 id>`（单件）；拒：多段、空段、`.`／`..`、查询串、绝对/协议相对 URL。
 * 用途＝`/api/models/<id>` 的 GET/PUT/DELETE（与集合 `/api/models` 同族、同源相对）。
 */
/**
 * ★F3d：**集合读面 + 受限查询串**——`path` 须逐字命中白名单，查询键须在该面的声明键集内
 * （`EGRESS_QUERY_KEYS`）；空值键**不出**（不造假值）。仍是同源相对路径，不出第二通道。
 */
export function assertEgressQuery(kind: EgressKind, path: string,
                                  params: Record<string, string | number | undefined> = {}): string {
  const base = assertEgress(kind, path);
  const allowed = new Set(EGRESS_QUERY_KEYS[kind] ?? []);
  const pairs: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    if (!allowed.has(key)) {
      throw new TransportError(E_CODES.EGRESS_FORBIDDEN, "egress",
        "查询键越出该读面声明：" + key, [...allowed].join("／"));
    }
    pairs.push(encodeURIComponent(key) + "=" + encodeURIComponent(String(value)));
  }
  return pairs.length ? base + "?" + pairs.join("&") : base;
}

export function assertEgressWithin(kind: EgressKind, path: string): string {
  const base = EGRESS_PATHS[kind];
  if (path === base) return base;
  const prefix = base + "/";
  if (!path.startsWith(prefix)) {
    throw new TransportError(E_CODES.EGRESS_FORBIDDEN, "egress",
      "出网目标越出白名单前缀：" + path, base + " 或 " + prefix + "<单段 id>");
  }
  const tail = path.slice(prefix.length);
  let okSegment = tail.length > 0 && !tail.includes("/") && tail !== "." && tail !== "..";
  // ★20261006：`<id>/<声明子资源>` 两段形（第二段须在 `EGRESS_SUBPATHS[kind]` 声明内）
  if (!okSegment) {
    const parts = tail.split("/");
    const subs = EGRESS_SUBPATHS[kind] ?? [];
    okSegment =
      parts.length === 2 &&
      parts[0].length > 0 && parts[0] !== "." && parts[0] !== ".." &&
      subs.includes(parts[1]);
  }
  if (!okSegment) {
    throw new TransportError(E_CODES.EGRESS_FORBIDDEN, "egress",
      "单件路径段非法（禁多段/穿越/空段）：" + path, prefix + "<单段 id>");
  }
  return path;
}
